import { useCallback, useState } from 'react';
import { WelcomeStage } from '@/components/character/WelcomeStage';
import { NamingStage } from '@/components/character/NamingStage';
import { ConfirmStage } from '@/components/character/ConfirmStage';
import { TransitionStage } from '@/components/character/TransitionStage';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth';
import { isReservedCharacterName } from '@/data/characterNames';
import { validateCharacterName } from '@/lib/validation';
import type { Character } from '@/types/database';

type Stage = 'welcome' | 'naming' | 'confirm' | 'transition';

interface CharacterCreationPageProps {
  onComplete: () => void;
}

export default function CharacterCreationPage({
  onComplete,
}: CharacterCreationPageProps): JSX.Element {
  const [stage, setStage] = useState<Stage>('welcome');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const setCharacter = useAuthStore((s) => s.setCharacter);

  const handleConfirm = useCallback(async (): Promise<void> => {
    setError(null);

    if (!user) {
      setError('Your session has expired. Sign in again to continue.');
      return;
    }

    // Belt and braces: re-run validation here even though the naming stage
    // already enforced it, in case state was tampered with.
    const trimmed = name.trim();
    const shapeError = validateCharacterName(trimmed);
    if (shapeError) {
      setError(shapeError);
      return;
    }
    if (isReservedCharacterName(trimmed)) {
      setError('That name is spoken for. Choose another.');
      return;
    }

    setLoading(true);

    try {
      const { data, error: insertError } = await supabase
        .from('characters')
        .insert({
          profile_id: user.id,
          name: trimmed,
          current_port: 'cutlass_cove',
        })
        .select('*')
        .single<Character>();

      if (insertError) {
        // 23505 = unique violation (the characters.name UNIQUE constraint).
        if (insertError.code === '23505') {
          setError('That name was just claimed. Choose another.');
        } else {
          setError(insertError.message || 'Could not create your character. Try again.');
        }
        return;
      }
      if (!data) {
        setError('Could not load your new character. Try again.');
        return;
      }

      setCharacter(data);
      setStage('transition');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }, [name, user, setCharacter]);

  if (stage === 'welcome') {
    return <WelcomeStage onContinue={() => setStage('naming')} />;
  }

  if (stage === 'naming') {
    return (
      <NamingStage
        initialName={name}
        onBack={() => setStage('welcome')}
        onContinue={(value) => {
          setName(value);
          setError(null);
          setStage('confirm');
        }}
      />
    );
  }

  if (stage === 'confirm') {
    return (
      <ConfirmStage
        name={name}
        loading={loading}
        error={error}
        onBack={() => {
          if (loading) return;
          setError(null);
          setStage('naming');
        }}
        onConfirm={() => {
          void handleConfirm();
        }}
      />
    );
  }

  return <TransitionStage onComplete={onComplete} />;
}
